'use client'

import ImageWithFallback from '@/components/common/image-with-fallback'
import ArrowUpRight45 from '@/components/icons/arrow-up-right-45'
import CalendarIcon2 from '@/components/icons/calendar-icon-2'
import CarIcon from '@/components/icons/car-icon'
import HollowCircle from '@/components/icons/hollow-circle'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { localDateTime } from '@/helpers/datetime'
import { getCurrentOrNextSession } from '@/helpers/events'
import { htmlClassName, normalizeHtml } from '@/helpers/html'
import dayjs from '@/lib/dayjs'
import { cn } from '@/lib/utils'
import { getInviteServiceDetails } from '@/resources/service'
import Link from 'next/link'
import { useMemo, useState } from 'react'
import { getInviteLink, isDiscordService } from '../utils'

function getCarClassDisplayName(carClass) {
  return carClass?.carClass?.name ?? carClass?.name ?? null
}

const InviteCard = ({ community }) => {
  if (!community || !community.joinLinkUrl) return null

  const inviteLink = getInviteLink(
    community.joinLinkUrl,
    community.joinLinkService,
  )

  const { name: inviteServiceName, Icon } = getInviteServiceDetails(
    community.joinLinkService,
  )

  const isDiscord = isDiscordService(community.joinLinkService)

  return (
    <div className="flex h-fit flex-col gap-5 rounded-lg border border-neutral-blue-700 bg-neutral-blue-800 px-10 py-[36px]">
      <div className="relative mx-auto size-7 overflow-hidden rounded-md bg-neutral-blue-100/20 desktop-xs:size-10">
        <ImageWithFallback
          alt="Community Logo"
          className="object-cover"
          src={community.logoImgUrl}
          fill
          fallbackUrl="/img/default/header.webp"
        />
      </div>
      <h5 className="text-center text-h-6 font-semibold desktop-xs:text-h-5">
        Join {community.name} {inviteServiceName}
      </h5>
      <Button
        variant="outline"
        asChild
        className={cn(
          'rounded-smpx-4 w-full! text-white! hover:bg-[#444cb3]! mx-auto mt-5 flex h-fit flex-nowrap items-center gap-[9px] py-5 align-middle font-inter font-semibold tablet:mb-4 tablet:ml-auto tablet:mr-0 tablet:mt-auto tablet:w-fit tablet:py-4',
          isDiscord
            ? `border-neutral-blue-600! bg-[#5B65EA]! hover:bg-[#444cb3]!`
            : `bg-linear-to-b hover:from-primary-600! hover:to-primary-600! from-primary-500 to-[#FF3D00]`,
        )}
      >
        <Link href={inviteLink} target="_blank">
          {Icon && <Icon className="size-5 fill-white" />} Join{' '}
          {inviteServiceName}
        </Link>
      </Button>
    </div>
  )
}

export default function DetailsSection({
  championship,
  events,
  eventsLoading,
}) {
  const [now] = useState(() => new Date())

  const startTime = useMemo(() => {
    if (
      !championship ||
      !championship.startDateTime ||
      !championship.targetTimezone
    )
      return null

    return localDateTime({
      datetime: championship.startDateTime,
      timezone: championship.targetTimezone,
      format: false,
    })
  }, [championship])

  const currentOrNextMemo = useMemo(() => {
    const currOrNextSession = getCurrentOrNextSession(events)

    if (
      !currOrNextSession ||
      !currOrNextSession.targetDateTime ||
      !currOrNextSession.targetTimezone
    )
      return { currentOrNextEvent: null, currentOrNextSessionDateTimeTz: null }

    const currentOrNextEvent = events.find(
      (e) => e.id === currOrNextSession.eventId,
    )

    const currentOrNextSessionDateTimeTz = localDateTime({
      datetime: currOrNextSession.targetDateTime,
      timezone: currOrNextSession.targetTimezone,
      format: false,
    })

    if (currentOrNextSessionDateTimeTz.isBefore(dayjs(now))) {
      return {
        currentOrNextEvent,
        currentOrNextSessionDateTimeTz: 'LIVE',
      }
    } else {
      return { currentOrNextEvent, currentOrNextSessionDateTimeTz }
    }
  }, [events, now])

  const { currentOrNextEvent, currentOrNextSessionDateTimeTz } =
    currentOrNextMemo

  // combine main community with other communities
  const communities = [
    championship.community,
    ...(championship.otherCommunities || []),
  ]
  const carClassNames = (championship.carClasses || [])
    .map(getCarClassDisplayName)
    .filter(Boolean)

  return (
    <div className="font-inter">
      <div className="relative w-full overflow-hidden rounded-md desktop-xs:hidden">
        <ImageWithFallback
          alt=""
          className="object-cover"
          src={championship.headerImgUrl}
          fallbackUrl="/img/default/header.webp"
          width={1920}
          height={1080}
        />
      </div>

      <div className="mt-[17px] grid grid-cols-2 gap-4 desktop-xs:mt-[26px] desktop-xs:grid-cols-4 desktop-xs:gap-[26px]">
        <div className="flex flex-col gap-2 rounded-md border border-neutral-blue-700 bg-neutral-blue-800 px-4 py-4 desktop-xs:gap-5 desktop-xs:px-[36px] desktop-xs:py-8">
          <div className="flex items-center gap-2">
            <div className="flex size-10 min-w-10 items-center justify-center rounded-sm bg-neutral-blue-700 desktop-xs:size-12">
              <CalendarIcon2 className="mx-auto size-6 stroke-white" />
            </div>
            <p className="text-p-1 font-semibold text-neutral-blue-300 desktop-xs:text-p-2">
              {startTime && startTime.isBefore(now) ? 'Started' : 'Starts'}
            </p>
          </div>
          {eventsLoading ? (
            <Skeleton className="h-[46px]" />
          ) : (
            <h6 className="text-p-2 font-semibold desktop-xs:text-h-6">
              {startTime
                ? startTime.format('ddd D MMMM • HH:mm z')
                : 'No start date set'}
            </h6>
          )}
        </div>

        <div className="flex flex-col gap-2 rounded-md border border-neutral-blue-700 bg-neutral-blue-800 px-4 py-4 desktop-xs:gap-5 desktop-xs:px-[36px] desktop-xs:py-8">
          <div className="flex items-center gap-2">
            <div className="flex size-10 min-w-10 items-center justify-center rounded-sm bg-neutral-blue-700 desktop-xs:size-12">
              <ArrowUpRight45 className="mx-auto size-6 stroke-white" />
            </div>
            <p className="text-p-1 font-semibold text-neutral-blue-300 desktop-xs:text-p-2">
              Next Race
            </p>
          </div>
          {eventsLoading ? (
            <Skeleton className="h-[46px]" />
          ) : (
            <h6 className="text-p-2 font-semibold desktop-xs:text-h-6">
              {currentOrNextSessionDateTimeTz &&
              currentOrNextSessionDateTimeTz === 'LIVE' ? (
                <div className="relative h-5 w-fit text-center font-bold">
                  <p className="absolute font-syncopate uppercase text-success">
                    LIVE
                  </p>
                  <p className="animate-[ping_2s_cubic-bezier(0,0,0.2,2)_infinite] font-syncopate uppercase text-success">
                    LIVE
                  </p>
                </div>
              ) : currentOrNextSessionDateTimeTz ? (
                currentOrNextSessionDateTimeTz.format('ddd D MMMM • HH:mm z')
              ) : (
                'No events scheduled'
              )}
            </h6>
          )}
        </div>

        <div className="flex flex-col gap-2 rounded-md border border-neutral-blue-700 bg-neutral-blue-800 px-4 py-4 desktop-xs:gap-5 desktop-xs:px-[36px] desktop-xs:py-8">
          <div className="flex items-center gap-2">
            <div className="flex size-10 min-w-10 items-center justify-center rounded-sm bg-neutral-blue-700 desktop-xs:size-12">
              <HollowCircle className="mx-auto size-6 stroke-white" />
            </div>
            <p className="text-p-1 font-semibold text-neutral-blue-300 desktop-xs:text-p-2">
              Events
            </p>
          </div>
          {eventsLoading ? (
            <Skeleton className="h-[46px]" />
          ) : (
            <h6 className="text-p-2 font-semibold desktop-xs:text-h-6">
              {currentOrNextEvent
                ? currentOrNextEvent.eventRoundNumber
                : events.length}{' '}
              of {events.length}
            </h6>
          )}
        </div>

        <div className="flex flex-col gap-2 rounded-md border border-neutral-blue-700 bg-neutral-blue-800 px-4 py-4 desktop-xs:gap-5 desktop-xs:px-[36px] desktop-xs:py-8">
          <div className="flex items-center gap-2">
            <div className="flex size-10 min-w-10 items-center justify-center rounded-sm bg-neutral-blue-700 desktop-xs:size-12">
              <CarIcon className="mx-auto size-6 stroke-white" />
            </div>
            <p className="text-p-1 font-semibold text-neutral-blue-300 desktop-xs:text-p-2">
              Car Classes
            </p>
          </div>
          {eventsLoading ? (
            <Skeleton className="h-[46px]" />
          ) : (
            <div className="flex gap-3 align-middle">
              <h6 className="truncate-2 w-fit text-p-2 font-semibold desktop-xs:text-h-6">
                {carClassNames.length > 0 ? carClassNames[0] : 'None Selected'}
              </h6>

              {carClassNames.length > 1 && (
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <p className="w-fit cursor-pointer text-p-2 text-neutral-blue-300 underline underline-offset-2">
                        +{carClassNames.length - 1} more
                      </p>
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="max-h-[300px] overflow-y-auto">
                        {carClassNames.slice(1).map((carClassName) => (
                          <div
                            key={`car-${carClassName}`}
                            className="rounded-sm px-2 py-2 text-p-1 font-semibold text-shades-0 transition-colors hover:bg-neutral-blue-700"
                          >
                            {carClassName}
                          </div>
                        ))}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mb-10 mt-10 grid min-w-0 gap-y-10 desktop-xs:mb-0 desktop-xs:mt-20 desktop-xs:grid-cols-10 desktop-xs:gap-x-4 desktop-sm:gap-x-6 desktop-md:gap-x-7 desktop-lg:gap-x-8">
        <div
          className={cn(
            htmlClassName,
            'wrap-anywhere col-span-full w-full min-w-0 max-w-full desktop-xs:col-span-7',
          )}
          dangerouslySetInnerHTML={{
            __html: championship.descriptionHtml
              ? normalizeHtml(championship.descriptionHtml)
              : 'No description has been added to this championship.',
          }}
        ></div>

        <div className="col-span-full flex min-w-0 flex-col gap-4 desktop-xs:col-span-3">
          {communities.map((comm) => (
            <InviteCard key={comm.id} community={comm} />
          ))}
        </div>
      </div>
    </div>
  )
}
